import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface DeserializeResult<T> {
  /** True if URL has any relevant params */
  hasAnyUrlParams: boolean;
  /** Full state with defaults. Must always be provided so that if sessionStorage is empty, we can initialize it. */
  state: T;
}

export interface UseSyncedStateOptions<T> {
  /** Unique key for sessionStorage */
  sessionStorageKey: string;

  /**
   * Convert state to URL params (only include non-default values).
   * Should be defined outside component or wrapped in useCallback.
   */
  serialize: (state: T) => URLSearchParams;

  /**
   * Parse URL params to full state with defaults.
   * Should be defined outside component or wrapped in useCallback.
   */
  deserialize: (params: URLSearchParams) => DeserializeResult<T>;

  /**
   * The set of URL param keys this hook owns. Omit when the hook owns the entire query string (the default).
   * Provide it when the hook shares the URL with state owned elsewhere (e.g. a sub-widget
   * Must be a superset of every key `serialize` can emit.
   */
  ownedKeys?: readonly string[];
}

/**
 * Syncs state between URL params and sessionStorage with priority: URL > sessionStorage > defaults.
 *
 * State is owned by useState — URL and sessionStorage are write-only side effects.
 * This means navigating away (unmount) can't corrupt sessionStorage, because no
 * effects fire on unmount.
 *
 * serialize/deserialize must be inverse operations to avoid sync issues.
 *
 * By default the hook owns the entire query string. Pass `ownedKeys` to share the
 * URL with state owned elsewhere — writes then merge instead of replacing the bag.
 *
 * @example
 * const [filters, updateFilters] = useSyncedState({
 *   sessionStorageKey: "table-filters",
 *   serialize: (state) => {
 *     const params = new URLSearchParams();
 *     if (state.search) params.set("q", state.search);
 *     if (state.page !== 1) params.set("page", state.page.toString());
 *     return params;
 *   },
 *   deserialize: (params) => {
 *     const partial: Partial<TableFilters> = {};
 *     const search = params.get("q");
 *     if (search) partial.search = search;
 *     const page = params.get("page");
 *     if (page) partial.page = parseInt(page);
 *
 *     const defaults = { search: "", page: 1 };
 *     return {
 *       hasAnyUrlParams: Object.keys(partial).length > 0,
 *       state: { ...defaults, ...partial },
 *     };
 *   },
 * });
 */
export function useSyncedState<T>({
  sessionStorageKey,
  serialize,
  deserialize,
  ownedKeys,
}: UseSyncedStateOptions<T>): [
  state: T,
  updateState: (updates: Partial<T>) => void,
] {
  const [, setSearchParams] = useSearchParams();

  // Write owned params to the URL. With ownedKeys, merge into the existing
  // query string (preserving foreign params); without it, replace the whole bag.
  const writeOwnedParams = useCallback(
    (ownedParams: URLSearchParams) => {
      if (!ownedKeys) {
        setSearchParams(ownedParams, { replace: true });
        return;
      }
      // Merge against the live URL (window.location), not react-router's
      // functional `prev`. `prev` is bound to the render, so two co-located
      // hooks writing in the same tick (e.g. both mount effects) would each see
      // the pre-write snapshot and clobber the other. window.location is kept
      // current synchronously by history.replaceState, so merges compose.
      const merged = mergeOwnedParams(
        new URLSearchParams(window.location.search),
        ownedParams,
        ownedKeys,
      );
      setSearchParams(merged, { replace: true });
    },
    [ownedKeys, setSearchParams],
  );

  // Initialize once: URL > sessionStorage > defaults
  const [state, setState] = useState<T>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlResult = deserialize(params);
    if (urlResult.hasAnyUrlParams) {
      try {
        sessionStorage.setItem(
          sessionStorageKey,
          serialize(urlResult.state).toString(),
        );
      } catch (e) {
        console.error(e);
      }
      return urlResult.state;
    }
    try {
      const raw = sessionStorage.getItem(sessionStorageKey);
      if (raw) {
        // Route sessionStorage through deserialize so values regain their
        // prototypes (e.g. Temporal.PlainDate) and missing fields fall back
        // to defaults — JSON.parse alone would yield plain strings/objects.
        return deserialize(new URLSearchParams(raw)).state;
      }
    } catch (e) {
      console.error(e);
    }
    return urlResult.state;
  });

  // On mount: sync restored state to URL (e.g. sessionStorage → URL params).
  // Mount-only so it can't fire during navigation.
  useEffect(() => {
    writeOwnedParams(serialize(state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateState = useCallback(
    (updates: Partial<T>) => {
      const newState = { ...state, ...updates } as T;
      setState(newState);
      const params = serialize(newState);
      writeOwnedParams(params);
      try {
        sessionStorage.setItem(sessionStorageKey, params.toString());
      } catch (e) {
        console.error(e);
      }
    },
    [state, serialize, writeOwnedParams, sessionStorageKey],
  );

  return [state, updateState];
}

/**
 * Merge this hook's serialized params into the existing query string, preserving
 * params owned elsewhere. Owned keys are cleared first so that a key absent from
 * `ownedParams` (because its value returned to default) is removed rather than
 * left stale. `ownedParams` keys must be a subset of `ownedKeys`.
 */
function mergeOwnedParams(
  existing: URLSearchParams,
  ownedParams: URLSearchParams,
  ownedKeys: readonly string[],
): URLSearchParams {
  const merged = new URLSearchParams(existing);
  for (const key of ownedKeys) {
    merged.delete(key);
  }
  for (const [key, value] of ownedParams) {
    merged.append(key, value);
  }
  return merged;
}
